import { useEffect, useState } from "react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";

export default function SettingsAdmin() {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);

    useEffect(() => {
        api.get("/admin/settings").then((r) => setForm(r.data)).catch(() => {});
        api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
        api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    }, []);

    if (!form) return <p className="text-slate-500">Loading settings…</p>;

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/settings", {
                low_stock_threshold: Number(form.low_stock_threshold),
                delivery_enabled: !!form.delivery_enabled,
                collection_enabled: !!form.collection_enabled,
                courier_delivery_enabled: !!form.courier_delivery_enabled,
                delivery_fee: Number(form.delivery_fee),
                courier_fee: Number(form.courier_fee ?? 0),
                free_delivery_over: Number(form.free_delivery_over),
                notification_email: form.notification_email || "",
                online_min_spend: Number(form.online_min_spend ?? 30),
                perishable_enabled: !!form.perishable_enabled,
                perishable_categories: form.perishable_categories || [],
                perishable_products: form.perishable_products || [],
                perishable_hold_hours: Number(form.perishable_hold_hours ?? 2),
            });
            setForm(data);
            toast.success("Settings saved");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="admin-settings" className="max-w-2xl space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
                <h2 className="font-heading font-extrabold text-lg text-slate-900">Fulfilment settings</h2>
                <div className="flex gap-6 flex-wrap">
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                        <input type="checkbox" checked={!!form.collection_enabled}
                            onChange={(e) => setForm({ ...form, collection_enabled: e.target.checked })}
                            className="accent-[#0033A0]" data-testid="settings-collection-enabled" />
                        Click & Collect available
                    </label>
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                        <input type="checkbox" checked={!!form.delivery_enabled}
                            onChange={(e) => setForm({ ...form, delivery_enabled: e.target.checked })}
                            className="accent-[#0033A0]" data-testid="settings-delivery-enabled" />
                        Local delivery available
                    </label>
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                        <input type="checkbox" checked={!!form.courier_delivery_enabled}
                            onChange={(e) => setForm({ ...form, courier_delivery_enabled: e.target.checked })}
                            className="accent-[#0033A0]" data-testid="settings-courier-enabled" />
                        Courier delivery available
                    </label>
                </div>
                <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="set-fee">Local delivery fee £</Label>
                        <Input id="set-fee" type="number" min="0" step="0.01" value={form.delivery_fee}
                            onChange={(e) => setForm({ ...form, delivery_fee: e.target.value })} data-testid="settings-delivery-fee" />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="set-free">Free local delivery over £</Label>
                        <Input id="set-free" type="number" min="0" step="0.01" value={form.free_delivery_over}
                            onChange={(e) => setForm({ ...form, free_delivery_over: e.target.value })} data-testid="settings-free-over" />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="set-courier">Courier delivery fee £ (flat)</Label>
                        <Input id="set-courier" type="number" min="0" step="0.01" value={form.courier_fee ?? 0}
                            onChange={(e) => setForm({ ...form, courier_fee: e.target.value })} data-testid="settings-courier-fee" />
                    </div>
                </div>
                <p className="text-xs text-slate-400">
                    Each product can also opt in/out of each method (Products → edit). Courier names and tracking links are entered per order in the Orders section. There is no live courier integration.
                </p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
                <h2 className="font-heading font-extrabold text-lg text-slate-900">Inventory</h2>
                <div className="space-y-1.5 max-w-xs">
                    <Label htmlFor="set-threshold">Low-stock alert threshold</Label>
                    <Input id="set-threshold" type="number" min="0" value={form.low_stock_threshold}
                        onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} data-testid="settings-threshold" />
                    <p className="text-xs text-slate-400">
                        Customers see "Only X left in stock" at or below this number; the dashboard alerts you at the same level.
                    </p>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
                <h2 className="font-heading font-extrabold text-lg text-slate-900">Online payments</h2>
                <div className="space-y-1.5 max-w-xs">
                    <Label htmlFor="set-minspend">Minimum spend for online (Stripe) payment £</Label>
                    <Input id="set-minspend" type="number" min="0" step="0.01" value={form.online_min_spend ?? 30}
                        onChange={(e) => setForm({ ...form, online_min_spend: e.target.value })} data-testid="settings-online-min" />
                    <p className="text-xs text-slate-400">
                        Customers below this can still order with "pay at the shop" (Click & Collect, no minimum).
                    </p>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4" data-testid="perishable-policy">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <h2 className="font-heading font-extrabold text-lg text-slate-900">Fresh & perishable collection policy</h2>
                        <p className="text-xs text-slate-500">
                            Selected categories/products are fresh and perishable: collection orders containing them are held for a limited time after the collection slot. Customers see this on the product page, at checkout and in their confirmation email.
                        </p>
                    </div>
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer shrink-0">
                        <input type="checkbox" checked={!!form.perishable_enabled}
                            onChange={(e) => setForm({ ...form, perishable_enabled: e.target.checked })}
                            className="accent-[#0033A0]" data-testid="perishable-enabled" />
                        Policy active
                    </label>
                </div>
                <div className="space-y-1.5 max-w-xs">
                    <Label htmlFor="set-hold">Hold time after collection slot (hours)</Label>
                    <Input id="set-hold" type="number" min="1" max="48" value={form.perishable_hold_hours ?? 2}
                        onChange={(e) => setForm({ ...form, perishable_hold_hours: e.target.value })} data-testid="perishable-hold-hours" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label className="mb-2 block">Categories under this policy</Label>
                        <div className="space-y-1.5 border border-slate-200 rounded-xl p-3 max-h-44 overflow-y-auto" data-testid="perishable-categories">
                            {categories.map((c) => (
                                <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="checkbox" className="accent-[#0033A0]"
                                        checked={(form.perishable_categories || []).includes(c.name)}
                                        onChange={() => {
                                            const list = form.perishable_categories || [];
                                            setForm({ ...form, perishable_categories: list.includes(c.name) ? list.filter((n) => n !== c.name) : [...list, c.name] });
                                        }}
                                        data-testid={`perishable-cat-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                                    {c.name}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label className="mb-2 block">Individual products under this policy</Label>
                        <div className="space-y-1.5 border border-slate-200 rounded-xl p-3 max-h-44 overflow-y-auto" data-testid="perishable-products">
                            {products.map((p) => (
                                <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="checkbox" className="accent-[#0033A0]"
                                        checked={(form.perishable_products || []).includes(p.id)}
                                        onChange={() => {
                                            const list = form.perishable_products || [];
                                            setForm({ ...form, perishable_products: list.includes(p.id) ? list.filter((id) => id !== p.id) : [...list, p.id] });
                                        }}
                                        data-testid={`perishable-prod-${p.id}`} />
                                    {p.name}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
                <h2 className="font-heading font-extrabold text-lg text-slate-900">Business</h2>
                <div className="space-y-1.5">
                    <Label htmlFor="set-notify">Order notification email (optional)</Label>
                    <Input id="set-notify" type="email" value={form.notification_email || ""}
                        onChange={(e) => setForm({ ...form, notification_email: e.target.value })}
                        placeholder="orders@yourshop.co.uk" data-testid="settings-notification-email" />
                    <p className="text-xs text-slate-400">Reserved for future order notifications to the shop.</p>
                </div>
            </div>

            <Button onClick={save} disabled={saving} className="rounded-full bg-[#0033A0] hover:bg-[#002277] px-8" data-testid="settings-save">
                {saving ? "Saving…" : "Save Settings"}
            </Button>
        </div>
    );
}
